/*
 * Copyright (c) 2022. Legalbird eine Marke der R&S Internet Jewels GmbH
 */

import abstractContractClaimTranslations from "./abstractContractClaimTranslations";
import { AbstractCase } from "../../types/Entities/AbstractCase";
import addressTranslations from "./addressTranslations";
import abstractLitigationCaseTranslations from "./abstractLitigationCaseTranslations";

const vehicleContractTranslations = {
  label: "Mietvertrag",
  reasonForDispute: {
    label: (caseData: AbstractCase) => (caseData.contractPartyType === "tenant" ? "Problem mit Vermieter" : "Problem mit Mieter"),
    question: "Welches Anliegen haben Sie bezüglich des Mietvertrags?",
    options: {
      housingDeficiencies: { label: "Mietmängel" },
      rentIncrease: {
        label: "Mieterhöhung",
        optionHintText: (caseData: AbstractCase) =>
          caseData.contractPartyType === "tenant" ? "" : "Ihr Mieter verweigert die Zustimmung zu einer von Ihnen angestrebten Erhöhung der Miete.",
      },
      utilitiesStatement: {
        label: "Nebenkostenabrechnung",
        optionHintText: (caseData: AbstractCase) =>
          caseData.contractPartyType === "tenant" ? "" : "Ihr Mieter verweigert die Nachzahlung der Nebenkostenabrechnung.",
      },
      deposit: {
        label: "Kaution",
        optionHintText: (caseData: AbstractCase) =>
          caseData.contractPartyType === "tenant" ? "" : "Ihr Mieter zahlt nicht die vereinbarte Mietsicherheit (Kaution).",
      },
      landlordHasCancelled: {
        label: "Kündigung",
      },
      illegalContractClause: {
        label: "Rechtswidrige Vertragsklausel",
      },
      rentReduction: {
        label: "Mietminderung",
        optionHintText: (caseData: AbstractCase) =>
          caseData.contractPartyType === "tenant"
            ? ""
            : "Ihr Mieter hat angekündigt, z.B. wegen angeblicher Mängel die Miete mindern zu wollen oder hat die Miete bereits gemindert.",
      },
      rentLate: {
        label: "Mietverzug",
        optionHintText: (caseData: AbstractCase) =>
          caseData.contractPartyType === "tenant" ? "" : "Ihr Mieter ist mit der Zahlung der Miete in Verzug oder verweigert die Zahlung.",
      },
      terminationRejection: {
        label: "Abgelehnte Kündigung",
        optionHintText: (caseData: AbstractCase) =>
          caseData.contractPartyType === "tenant" ? "" : "Ihr Mieter weigert sich, eine von Ihnen angestrebte Kündigung des Mietverhältnisses zu akzeptieren.",
      },
      depositPayout: {
        label: "Kautionserstattung",
        optionHintText: (caseData: AbstractCase) =>
          caseData.contractPartyType === "tenant" ? "" : "Ihr Mieter verlangt aus Ihrer Sicht unberechtigt eine (teilweise) Erstattung der Kaution.",
      },
      other: { label: "Anderes Anliegen" },
    },
  },
  contractPartyType: {
    question: "Sind Sie bei dem betroffenen Mietvertrag Mieter oder Vermieter?",
    options: {
      tenant: { label: "Mieter" },
      landlord: { label: "Vermieter" },
    },
  },

  hasPropertyManager: {
    question: (caseData: AbstractCase) =>
      caseData.contractPartyType === "landlord"
        ? "Gibt es eine Hausverwaltung, die Sie gegenüber Ihrem Mieter vertritt?"
        : "Gibt es eine Hausverwaltung, die Ihren Vermieter vertritt und mit der Sie entsprechend zu allen Anliegen kommunizieren?",
    options: {
      yes: {
        label: "Ja",
      },
      no: {
        label: "Nein",
      },
    },
  },
  housingDeficienciesDescriptionClient: {
    question: "Bitte beschreiben Sie hier die Mängel so genau wie möglich",
    label: "Mängelbeschreibung",
    helperText:
      "Beschreiben Sie möglichst detailliert, damit wir eine gute Grundlage für das rechtliche Vorgehen haben und führen Sie mehrere Mängel in einer Liste mit Spiegelstrichen auf.",
  },
  previousBasicRent: {
    question: (caseData: AbstractCase) => {
      if (caseData.contractPartyType === "landlord") {
        return "Wie hoch war die zuvor vereinbarte Kaltmiete (vor der von Ihnen angestrebten Mieterhöhung)?";
      }
      let prefix = "Wie hoch ist Ihre Kaltmiete zuvor gewesen?";

      if (caseData.reasonForDispute === "housingDeficiencies") {
        prefix = "Wie hoch ist Ihre derzeitige Kaltmiete?";
      }
      return (
        prefix +
        " Dies ist der Betrag, der ohne jegliche weitere Kosten (wie zum Beispiel für Heiz- oder Hausmeisterkosten) für die Miete der Räumlichkeiten vereinbart wurde."
      );
    },
    label: (caseData: AbstractCase) => (caseData.contractPartyType === "landlord" ? "Höhe bisherige Kaltmiete" : "Höhe vereinbarte Kaltmiete"),
    helperText: "Wenn Sie den Betrag gerade nicht genau benennen können, schauen Sie bitte im Mietvertrag nach oder lassen Sie das Feld leer.",
  },
  futureBasicRent: {
    question: (caseData: AbstractCase) =>
      caseData.contractPartyType === "landlord"
        ? "Wie hoch soll die von Ihnen angestrebte neue Kaltmiete sein?"
        : "Auf welchen Betrag möchte Ihr Vermieter die Kaltmiete nun erhöhen? Dies ist der Betrag den Ihr Vermieter nun ohne jegliche weitere Kosten (wie zum Beispiel für Heiz- oder Hausmeisterkosten) für die Miete der Räumlichkeiten verlangt.",
    label: "Höhe der neuen Kaltmiete",
    helperText: (caseData: AbstractCase) =>
      caseData.contractPartyType === "landlord"
        ? "Wenn Sie sich noch nicht sicher sind, auf welchen Betrag Sie die Miete erhöhen möchten, geben Sie einfach einen ungefähren Betrag an oder lassen Sie das Feld leer"
        : "Wenn Sie den Betrag gerade nicht genau benennen können, prüfen Sie bitte das Schreiben Ihres Vermieters zur Mieterhöhung oder lassen Sie zur Not das Feld leer.",
  },
  rentalContractStartDate: {
    question: "Wann hat das Mietverhältnis begonnen?",
    label: "Start Mietverhältnis",
  },
  rentalContractEndDate: {
    question: "Wann endet(e) das Mietverhältnis?",
    label: "Ende Mietverhältnis",
    helperText: (caseData: AbstractCase) =>
      ["rentIncrease", "utilitiesStatement"].includes(caseData.reasonForDispute)
        ? "Wenn das Mietverhältnis noch besteht, lassen Sie das Feld gerne einfach leer."
        : "",
  },
  previousRentIncludingHeating: {
    question: (caseData: AbstractCase) =>
      caseData.contractPartyType === "landlord"
        ? "Wie hoch war die zuvor vereinbarte Warmmiete (inklusive z.B. Heiz- oder Hausmeisterkosten)?"
        : "Wie hoch ist Ihre Warmmiete zuvor gewesen? Dies ist der Betrag, den Sie monatlich an Gesamtkosten (inklusive z.B. Heiz- oder Hausmeisterkosten) an Ihren Vermieter bezahlen.",
    label: (caseData: AbstractCase) => (caseData.contractPartyType === "landlord" ? "Höhe bisherige Warmmiete" : "Höhe vereinbarte Warmmiete"),
    helperText: "Wenn Sie den Betrag gerade nicht genau benennen können, schauen Sie bitte im Mietvertrag nach oder lassen Sie das Feld leer.",
  },
  futureRentIncludingHeating: {
    question: (caseData: AbstractCase) =>
      caseData.contractPartyType === "landlord"
        ? "Wie hoch soll die von Ihnen angestrebte neue Warmmiete (inklusive z.B. Heiz- oder Hausmeisterkosten) sein?"
        : "Auf welchen Betrag wird Ihre Warmmiete nun ansteigen? Dies ist der Betrag, den Sie nach der Mieterhöhung monatlich an Gesamtkosten (inklusive z.B. Heiz- oder Hausmeisterkosten) an Ihren Vermieter bezahlen müssten.",
    label: "Höhe der neuen Warmmiete",
    helperText: (caseData: AbstractCase) =>
      caseData.contractPartyType === "landlord"
        ? "Wenn Sie sich noch nicht sicher sind, auf welchen Betrag Sie die Miete erhöhen möchten, geben Sie einfach einen ungefähren Betrag an oder lassen Sie das Feld leer"
        : "Wenn Sie den Betrag gerade nicht genau benennen können, prüfen Sie bitte das Schreiben Ihres Vermieters zur Mieterhöhung oder lassen Sie zur Not das Feld leer.",
  },
  depositAmount: {
    question: "Welcher Betrag wurde als Kaution vereinbart?",
    label: "Höhe der Kaution",
  },
  cancellationReceivedDate: {
    question: "Wann haben Sie die Kündigung von Ihrem Vermieter erhalten?",
    label: "Eingang der Kündigung",
  },
  ...abstractContractClaimTranslations,
  //the following translations overwrite default contract law translations
  opposingPartyType: {
    question: (caseData: AbstractCase) =>
      caseData.contractPartyType === "landlord"
        ? "Handelt es sich bei Ihrem Mieter um eine bzw. mehrere Privatpersonen oder ein Unternehmen?"
        : "Handelt es sich bei Ihrem Vermieter um eine Privatperson oder ein Unternehmen?",
    options: {
      opposingPartyPerson: { label: "Privatperson" },
      opposingPartyLegalEntity: { label: "Unternehmen" },
    },
  },
  opposingPartyLegalEntity: {
    ...abstractLitigationCaseTranslations.opposingPartyLegalEntity,
    question: "Wie heißt das Unternehmen, gegen das Sie rechtlich vorgehen möchten?",
  },
  opposingPartyPerson: {
    headline: (caseData: AbstractCase) => {
      if (caseData.contractPartyType === "landlord") {
        if (caseData.opposingPartyCount === "multiple") {
          return "Wie heißt die 1. im Mietvertrag gelistete Person? Geben Sie hier gerne diejenige Person an, mit der sie hauptsächlich kommunizieren.";
        }
        return "Wie heißt Ihr Mieter?";
      } else {
        return "Wie heißt Ihr Vermieter?";
      }
    },
    question: (caseData: AbstractCase) => {
      if (caseData.contractPartyType === "landlord") {
        if (caseData.opposingPartyCount === "multiple") {
          return "Bitte geben Sie möglichst alle weiteren Daten an, die Ihnen vorliegen, damit wir schnellstmöglich für Sie tätig werden können.";
        }
        return "Bitte geben Sie die aktuelle Anschrift Ihres Mieters sowie die Kontaktdaten an, die Ihnen vorliegen, damit wir schnellstmöglich für Sie tätig werden können.";
      } else {
        return "Bitte geben Sie möglichst alle weiteren Daten an, die Ihnen vorliegen. Sollten Ihnen nicht alle Daten vorliegen, lassen Sie die Felder gerne einfach leer.";
      }
    },
  },
  contractWasTakenOverByOpposingParty: {
    label: "Anderer ursprünglicher Vermieter",
    question: (caseData: AbstractCase) =>
      caseData.contractPartyType === "landlord"
        ? "Wurde der Mietvertrag von Anfang an von dem aktuellen Vermieter abgeschlossen?"
        : "Wurde der Mietvertrag ursprünglich von einer anderen Partei als dem aktuellen Vermieter abgeschlossen?",
    options: {
      yes: { label: "Ja" },
      no: { label: "Nein" },
    },
  },
  affectedPartyType: {
    question: (caseData: AbstractCase) =>
      caseData.contractWasTakenOverByOpposingParty === "no" && caseData.contractPartyType === "landlord"
        ? "Wer ist aktuell als Vermieter im Vertrag aufgeführt?"
        : "Wer hat den Mietvertrag abgeschlossen?",
    options: {
      customer: {
        label: (caseData: AbstractCase) =>
          caseData.contractWasTakenOverByOpposingParty === "no" && caseData.contractPartyType === "landlord"
            ? "Ich stehe als Vermieter im Vertrag"
            : "Ich habe den Vertrag selbst abgeschlossen",
      },
      coinsured: {
        label: (caseData: AbstractCase) =>
          caseData.contractWasTakenOverByOpposingParty === "no" && caseData.contractPartyType === "landlord"
            ? "Eine andere Person, die mir bekannt ist, steht als Vermieter im Vertrag - zum Beispiel ein Familienmitglied"
            : "Der Vertrag wurde von jemand anderem abgeschlossen, der mir bekannt ist - zum Beispiel ein Familienmitglied",
      },
    },
  },
  contractDate: {
    label: "Datum des Vertrags",
    helperText: (caseData: AbstractCase) =>
      caseData.contractPartyType === "tenant"
        ? "Bitte tragen Sie hier ein, wann Sie den Vertrag unterzeichnet haben."
        : "Bitte tragen Sie hier ein, wann der Vertrag unterzeichnet wurde.",
  },
  claimedAmountClient: {
    label: "Forderung in €",
    question: (caseData: AbstractCase) =>
      caseData.contractPartyType === "tenant"
        ? "Wie viel Geld fordert Ihr Vermieter? Sollten Sie die Forderung (noch) nicht genau kennen, geben Sie hier eine Schätzung oder einfach “0” an."
        : "Wie viel Geld fordert Ihr Mieter? Sollten Sie die Forderung (noch) nicht genau kennen, geben Sie hier eine Schätzung oder einfach “0” an.",
    helperText:
      "Bitte geben Sie den Wert in Euro und Cent ein - zum Beispiel 136,15 oder 85,00. Wenn Sie den Betrag noch nicht genau wissen, lassen Sie das Feld gerne einfach leer",
  },
  defenseProcessStatus: {
    label: "Forderungsstand",
    question: (caseData: AbstractCase) => {
      return caseData.contractPartyType === "tenant"
        ? "Wie ist der Stand der Forderung Ihres Vermieters gegen Sie gerade?"
        : "Wie ist der Stand der Forderung Ihres Mieters gegen Sie gerade?";
    },
    options: {
      none: {
        label: (caseData: AbstractCase) => {
          return caseData.contractPartyType === "tenant"
            ? "Ich habe bisher nichts von dem Vermieter gehört, fürchte aber, dass dies noch passieren wird"
            : "Ich habe bisher nichts von dem Mieter gehört, fürchte aber, dass dies noch passieren wird";
        },
      },
      paymentOrder: {
        label: "Ich habe einen Mahnbescheid erhalten (gelber Brief per Post)",
      },
      enforcementOrder: {
        label: "Ich habe einen Vollstreckungsbescheid erhalten (gelber Brief per Post)",
      },
      debtCollection: {
        label: "Der Vermieter hat ein Inkasso-Unternehmen eingeschaltet, von dem ich zur Zahlung aufgefordert oder gemahnt wurde",
      },
      reminder: {
        label: (caseData: AbstractCase) => {
          return caseData.contractPartyType === "tenant"
            ? "Ich habe ein Forderungsschreiben von dem Vermieter erhalten"
            : "Ich habe ein Forderungsschreiben von dem Mieter erhalten";
        },
      },
      lawsuit: {
        label: "Ich habe eine Klage vom Gericht erhalten",
      },
    },
  },
  caseCategory: {
    question: "Wie können wir Ihnen weiterhelfen?",
    options: {
      enforceClaim: {
        label: "Ich möchte eine Forderung durchsetzen",
        optionHintText: (caseData: AbstractCase) =>
          caseData.contractPartyType === "tenant"
            ? "Der Mietvertrag wurde nicht wie vereinbart erfüllt und ich möchte dies nun einfordern oder Geld zurück verlangen / die Miete mindern. Beispiele: Es liegt ein Mietmangel vor, der behoben werden muss. | Ich habe zu viel Geld bezahlt oder meine Kaution nicht vollständig zurück erhalten und möchte daher Geld zurück verlangen. | Ich bin der Meinung, dass der Mietvertrag nicht rechtmäßig ist und möchte dagegen vorgehen."
            : "Der Mietvertrag wurde durch den Mieter nicht wie vereinbart erfüllt und ich möchte dies nun einfordern. Beispiele: Der Mieter zahlt die Miete / die Nebenkostenabrechnung / die Kautionszahlung nicht, ich möchte dem Mieter kündigen, der Mieter geht mit der Mietsache nicht ordnungsgemäß um.",
      },
      defendClaim: {
        label: "Ich möchte mich gegen eine Forderung wehren",
        optionHintText: (caseData: AbstractCase) =>
          caseData.contractPartyType === "tenant"
            ? "Von mir wird unberechtigterweise etwas eingefordert und ich möchte mich dagegen wehren. Beispiele: Der Vermieter verlangt etwas von mir, das mir rechtswidrig erscheint. | Ich soll meiner Meinung nach zu viel oder allgemein unrechtmäßig Geld bezahlen."
            : "Von mir wird unberechtigterweise etwas eingefordert und ich möchte mich dagegen wehren. Beispiele: Der Mieter verlangt etwas (Renovierung / Schönheitsreparatur / Erstattung von Zahlungen) von mir, das mir rechtswidrig erscheint.",
      },
      advisory: {
        label: "Ich benötige zunächst nur eine Erstberatung",
        optionHintText: "Ich möchte nichts weiter veranlassen, sondern habe nur eine kurze Frage im Bereich Mietrecht und benötige hierfür eine Erstberatung.",
      },
    },
  },
  subjectOfDispute: {
    label: "Forderung",
    question: (caseData: AbstractCase) => {
      switch (caseData.caseCategory) {
        case "enforceClaim":
          switch (caseData.contractPartyType) {
            case "tenant":
              return "Möchten Sie Geld oder eine Leistung seitens des Vermieters einfordern?";
            case "landlord":
              return "Möchten Sie Geld oder eine Leistung seitens des Mieters einfordern?";
            default:
              return "";
          }
        case "defendClaim":
          switch (caseData.contractPartyType) {
            case "tenant":
              return "Was fordert Ihr Vermieter von Ihnen?";
            case "landlord":
              return "Was fordert Ihr Mieter von Ihnen?";
            default:
              return "";
          }
      }
    },
    options: {
      money: {
        label: (caseData: AbstractCase) => {
          switch (caseData.caseCategory) {
            case "enforceClaim":
              return "Ich möchte Geld einfordern";
            case "defendClaim":
              switch (caseData.contractPartyType) {
                case "tenant":
                  return "Der Vermieter fordert Geld von mir";
                case "landlord":
                  return "Der Mieter fordert Geld von mir";
                default:
                  return "";
              }
          }
        },
      },
      moneyAndGoods: {
        label: (caseData: AbstractCase) => {
          switch (caseData.caseCategory) {
            case "enforceClaim":
              return "Ich möchte Geld fordern und auch eine Leistung";
            case "defendClaim":
              switch (caseData.contractPartyType) {
                case "tenant":
                  return "Mein Vermieter möchte Geld von mir und auch eine Leistung";
                case "landlord":
                  return "Der Mieter möchte Geld von mir und auch eine Leistung";
                default:
                  return "";
              }
          }
        },
      },
      goods: {
        label: (caseData: AbstractCase) => {
          switch (caseData.caseCategory) {
            case "enforceClaim":
              switch (caseData.contractPartyType) {
                case "tenant":
                  return "Ich möchte eine Leistung oder ein gewisses Verhalten beim Vermieter einfordern";
                case "landlord":
                  return "Ich möchte eine Leistung oder ein gewisses Verhalten vom Mieter einfordern";
                default:
                  return "";
              }
            case "defendClaim":
              switch (caseData.contractPartyType) {
                case "tenant":
                  return "Der Vermieter fordert eine Leistung oder ein gewisses Verhalten von mir";
                case "landlord":
                  return "Der Mieter fordert eine Leistung oder ein gewisses Verhalten von mir";
                default:
                  return "";
              }
          }
        },
      },
      other: {
        label: (caseData: AbstractCase) =>
          caseData.caseCategory === "enforceClaim" ? "Ich fordere etwas ganz anderes" : "Der Vermieter fordert etwas anderes",
      },
    },
  },
  clientHasAlreadyClaimed: {
    label: "Schriftliche Forderung",
    question: (caseData: AbstractCase) => {
      switch (caseData.contractPartyType) {
        case "tenant":
          return "Haben Sie bereits schriftlich eine Forderung an Ihren Vermieter gestellt?";
        case "landlord":
          return "Haben Sie bereits schriftlich eine Forderung an Ihren Mieter gestellt?";
        default:
          return "";
      }
    },
    options: {
      yes: {
        label: "Ja, ich habe bereits etwas eingefordert.",
      },
      no: {
        label: "Nein, ich habe bisher nichts eingefordert.",
      },
    },
  },
  contractAddressIsCurrentAddress: {
    label: "Aktuelle Adresse",
    question: (caseData: AbstractCase) => {
      return caseData.contractPartyType === "tenant"
        ? "Bezieht sich der betroffene Mietvertrag auf Ihre derzeitige Wohnadresse?"
        : "Handelt es sich bei der aktuellen Anschrift um die von Ihnen vermietete Immobilie?";
    },
    options: {
      yes: { label: "Ja" },
      no: { label: "Nein" },
    },
  },
  propertyAddress: {
    ...addressTranslations,
    helperText: (caseData: AbstractCase) =>
      caseData.contractPartyType === "tenant"
        ? "Wenn sich Ihre Wohnung in einem Mehrfamilienhaus befindet, geben Sie bitte zur eindeutigen Zuordnung die Etage, Wohneinheit o.Ä. an"
        : "Wenn sich die Wohnung in einem Mehrfamilienhaus befindet, geben Sie bitte die Etage, Wohneinheit o.Ä. an",
  },
  contractualPartnerHasLawyer: {
    label: (caseData: AbstractCase) => (caseData.contractPartyType === "tenant" ? "Vertragspartner anwaltlich vertreten" : "Mieter anwaltlich vertreten"),
    question: (caseData: AbstractCase) =>
      caseData.contractPartyType === "tenant"
        ? "Hat Ihr Vertragspartner bereits einen Anwalt eingeschaltet? Wenn Sie hier unsicher sind, oder bisher kein Schreiben von der Anwaltskanzlei erhalten haben, geben Sie hier einfach “Nein” an."
        : "Hat Ihr Mieter bereits einen Anwalt eingeschaltet? Wenn Sie hier unsicher sind, oder bisher kein Schreiben von der Anwaltskanzlei erhalten haben, geben Sie hier einfach “Nein” an.",
    options: {
      yes: {
        label: "Ja",
      },
      no: {
        label: "Nein",
      },
    },
  },
};

export default vehicleContractTranslations;
